import axios, { AxiosError } from 'axios';
import { URL } from '../../config/config';
import { replaceDomainInData } from './mediaDomain';
import {
  ClaimResultResponse,
  DraftGenerateBody,
  GenerateResponse,
  GeneratedTemplate,
  GeneratePromptFields,
  InitiateResponse,
  ResultResponse,
  UploadUrlResponse,
  VerifyOtpResponse,
} from './adsGenerator.types';

// link-gen-backend's public /public/ads routes (ADS-266). The phone number is
// the lead's key; after OTP every call also sends the session token. Ads are
// generated by the Ads Agent pipeline, so the page starts a job and polls.
const BASE = `${URL}/public/ads`;
const SESSION_HEADER = 'x-ads-session-token';

// Runs the page end-to-end without a backend (OTP 123456).
const IS_MOCK = process.env.REACT_APP_ADS_GENERATOR_MOCK === 'true';

export const SIGNUP_URL = 'https://app.aisensy.com/signup';

type InitiateBody = {
  name: string;
  email: string;
  phone: string;
  state: string;
  prompt: string;
  aspectRatio: string;
  adType: string;
  adCount: number;
};

type ApiError = { message?: string; code?: string; templateId?: string | null };

export const getErrorMessage = (err: unknown): string => {
  const axiosError = err as AxiosError<ApiError>;
  if (axiosError?.response?.data?.message) {
    return axiosError.response.data.message;
  }
  // A server error page (e.g. 503 HTML) or a network failure has no readable
  // message for the visitor.
  if (axiosError?.isAxiosError || err instanceof TypeError) {
    return 'Something went wrong. Please try again.';
  }
  if ((err as Error)?.message) {
    return (err as Error).message;
  }
  return 'Something went wrong. Please try again.';
};

export const getErrorCode = (err: unknown): string =>
  (err as AxiosError<ApiError>)?.response?.data?.code || '';

const authHeaders = (sessionToken: string) => ({ [SESSION_HEADER]: sessionToken });

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

// ---- mock mode: a generation that walks the worker's stages over ~25s ----
const MOCK_STEPS = [
  { stage: 'Queued', percent: 0 },
  { stage: 'Reading your prompt', percent: 10 },
  { stage: 'Choosing the ad types', percent: 30 },
  { stage: 'Designing the creative', percent: 60 },
  { stage: 'Putting it together', percent: 90 },
];
let mockStartedAt = 0;
// Finished mock generations, newest first, like the backend's `templates`.
let mockHistory: GeneratedTemplate[] = [];
let mockAdCount = 3;
let mockAspectRatio = '3:4';

const mockImage = (index: number): string => {
  const colors = ['#0a474c', '#42B864', '#7BA8A3'];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800"><rect width="600" height="800" fill="${
    colors[index % colors.length]
  }"/><text x="300" y="410" font-size="48" text-anchor="middle" fill="white" font-family="Inter">Ad ${
    index + 1
  }</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const mockTemplate = (): GeneratedTemplate => {
  const step = Math.floor((Date.now() - mockStartedAt) / 5000);
  const done = step >= MOCK_STEPS.length;
  return {
    _id: `mock-template-${mockStartedAt}`,
    status: done ? 'completed' : 'processing',
    templateCount: mockAdCount,
    aspectRatio: mockAspectRatio,
    progress: done ? { stage: 'complete', percent: 100 } : MOCK_STEPS[step],
    variants: done
      ? Array.from({ length: mockAdCount }, (_, i) => ({
          _id: `mock-${mockStartedAt}-${i}`,
          variantIndex: i,
          variantStatus: 'completed',
          adType: i === 1 ? 'website_to_whatsapp' : 'ctwa',
          adName: `Mock ad ${i + 1}`,
          headline: `Headline for ad ${i + 1}`,
          headlineDescription: 'A short supporting line',
          adDescription: 'Primary text the worker writes for this ad.',
          callToAction: i === 1 ? 'SHOP_NOW' : 'WHATSAPP_MESSAGE',
          websiteUrl: i === 1 ? 'https://example.com' : '',
          imageUrl: mockImage(i),
          aspectRatio: mockAspectRatio,
          minAge: 24,
          maxAge: 45,
          geoLocationDisplay: [{ key: '1728', name: 'Delhi', type: 'region' }],
          selected: { budgetType: 'daily_budget', dailyBudget: 800, durationDays: 7 },
        }))
      : [],
  };
};
// --------------------------------------------------------------------------

export const initiate = async (body: InitiateBody): Promise<InitiateResponse> => {
  if (IS_MOCK) {
    await wait(800);
    mockAdCount = body.adCount;
    mockAspectRatio = body.aspectRatio;
    return { phone: body.phone, otpChannels: ['whatsapp', 'email'] };
  }
  const res = await axios.post<InitiateResponse>(`${BASE}/initiate`, body);
  return res.data;
};

export const verifyOtp = async (
  phone: string,
  otp: string,
  claimToken?: string,
): Promise<VerifyOtpResponse> => {
  if (IS_MOCK) {
    await wait(800);
    if (otp !== '123456') {
      throw new Error('Incorrect OTP. Use 123456 in mock mode.');
    }
    return {
      sessionToken: 'mock-token',
      expiresInSec: 3600,
      status: 'otp_verified',
      templateId: null,
      generationsLeft: 1,
    };
  }
  const res = await axios.post<VerifyOtpResponse>(`${BASE}/verify-otp`, {
    phone,
    otp,
    ...(claimToken ? { claimToken } : {}),
  });
  return res.data;
};

const postToS3 = async (uploadURL: UploadUrlResponse['uploadURL'], file: File): Promise<void> => {
  const form = new FormData();
  Object.entries(uploadURL.fields).forEach(([key, value]) => form.append(key, value));
  form.append('file', file);
  const upload = await fetch(uploadURL.url, { method: 'POST', body: form });
  if (!upload.ok) {
    throw new Error('Could not upload your image. Please try again.');
  }
};

// Uploads a product image to S3 with a signed form and returns its URL.
export const uploadImage = async (
  phone: string,
  sessionToken: string,
  file: File,
): Promise<string> => {
  if (IS_MOCK) {
    await wait(600);
    return `https://mock.local/IMAGE/public-ads/mock/${file.name}`;
  }
  const { data } = await axios.post<UploadUrlResponse>(
    `${BASE}/upload-url`,
    { phone, fileName: file.name, contentType: file.type },
    { headers: authHeaders(sessionToken) },
  );
  await postToS3(data.uploadURL, file);
  return data.fullUrl;
};

// Before OTP: the first upload returns a draft token that later uploads and generate reuse.
export const uploadDraftImage = async (
  file: File,
  draftToken: string | null,
): Promise<{ url: string; draftToken: string }> => {
  const { data } = await axios.post<UploadUrlResponse>(`${BASE}/upload-url`, {
    fileName: file.name,
    contentType: file.type,
    ...(draftToken ? { draftToken } : {}),
  });
  await postToS3(data.uploadURL, file);
  return { url: data.fullUrl, draftToken: data.draftToken || draftToken || '' };
};

export const startDraftGeneration = async (body: DraftGenerateBody): Promise<GenerateResponse> => {
  const res = await axios.post<GenerateResponse>(
    `${BASE}/generate`,
    { ...body, productImageUrls: replaceDomainInData(body.productImageUrls) },
  );
  return res.data;
};

export const getClaimResult = async (claimToken: string): Promise<ClaimResultResponse> => {
  const res = await axios.get<ClaimResultResponse>(`${BASE}/result`, { params: { claimToken } });
  return res.data;
};

// Starts the Ads Agent job; progress and ads come from getResult.
export const startGeneration = async (
  phone: string,
  sessionToken: string,
  productImageUrls: string[],
  promptFields?: GeneratePromptFields,
): Promise<GenerateResponse> => {
  if (IS_MOCK) {
    await wait(500);
    if (mockStartedAt) {
      mockHistory = [mockTemplate(), ...mockHistory];
    }
    mockStartedAt = Date.now();
    return { templateId: 'mock-template', status: 'generating' };
  }
  const res = await axios.post<GenerateResponse>(
    `${BASE}/generate`,
    { phone, productImageUrls: replaceDomainInData(productImageUrls), ...promptFields },
    { headers: authHeaders(sessionToken) },
  );
  return res.data;
};

export const getResult = async (phone: string, sessionToken: string): Promise<ResultResponse> => {
  if (IS_MOCK) {
    const template = mockStartedAt ? mockTemplate() : null;
    return {
      status: template && template.status === 'completed' ? 'completed' : 'generating',
      waMessageSent: Boolean(template && template.status === 'completed'),
      template,
      templates: template ? [template, ...mockHistory] : [],
    };
  }
  const res = await axios.get<ResultResponse>(`${BASE}/result`, {
    params: { phone },
    headers: authHeaders(sessionToken),
  });
  return res.data;
};
